import React,{createContext,useContext,useState,useEffect} from 'react';
import {useSupabase} from './SupabaseContext';
import toast from 'react-hot-toast';

const AuthContext=createContext({});

export const useAuth=()=> {
  const context=useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider=({children})=> {
  const [user,setUser]=useState(null);
  const [loading,setLoading]=useState(true);
  const supabase=useSupabase();

  useEffect(()=> {
    const getSession=async ()=> {
      const {data: {session}}=await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    const {data: {subscription}}=supabase.auth.onAuthStateChange(
      async (event,session)=> {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return ()=> subscription.unsubscribe();
  },[supabase.auth]);

  const signUp=async (email,password,name)=> {
    try {
      const {data,error}=await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
          },
        },
      });
      if (error) throw error;
      // The handle_new_user trigger will now create the profile automatically.
      // No need for a client-side insert.
      toast.success(
        'Account created successfully! Please check your email to verify your account.'
      );
      return {data,error: null};
    } catch (error) {
      toast.error(error.message);
      return {data: null,error};
    }
  };

  const signIn=async (email,password)=> {
    try {
      const {data,error}=await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          toast.error(
            'Please verify your email before signing in. Check your inbox for the verification link.'
          );
          return {data: null,error};
        }
        throw error;
      }
      toast.success('Welcome back!');
      return {data,error: null};
    } catch (error) {
      toast.error(error.message);
      return {data: null,error};
    }
  };

  const signOut=async ()=> {
    try {
      const {error}=await supabase.auth.signOut();
      if (error) throw error;
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetPassword=async (email)=> {
    try {
      const {error}=await supabase.auth.resetPasswordForEmail(email,{
        redirectTo: `${window.location.origin}/#/reset-password`,
        captchaToken: undefined, // Explicitly set to undefined to avoid captcha issues
      });
      if (error) {
        console.error('Password reset error:',error);
        throw error;
      }
      toast.success('Password reset link sent! Please check your email.');
    } catch (error) {
      console.error('Password reset error details:',error);
      toast.error(error.message || 'Failed to send password reset email');
    }
  };

  const resetPasswordNew=async (newPassword)=> {
    try {
      const {error}=await supabase.auth.updateUser({password: newPassword});
      if (error) throw error;
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const verifyEmail=async (token)=> {
    try {
      const {error}=await supabase.auth.verifyOtp({token,type: 'signup'});
      if (error) throw error;
      toast.success('Email verified successfully!');
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const resendVerificationEmail=async (email)=> {
    try {
      const {error}=await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });
      if (error) throw error;
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const value={
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resetPasswordNew,
    verifyEmail,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};